import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, CalendarDays, Link as LinkIcon, Unlink, RefreshCw, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysPrev = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) {
    cells.push({ day: daysPrev - startDay + i + 1, outside: true, date: null });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, outside: false, date: iso });
  }
  let nextDay = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ day: nextDay++, outside: true, date: null });
  }
  return cells;
}

function eventDateKey(ev) {
  const s = ev.start || "";
  return s.slice(0, 10);
}

function eventTimeLabel(ev) {
  if (ev.all_day) return "Tutto il giorno";
  const d = new Date(ev.start);
  return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

export default function Shooting() {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [connected, setConnected] = useState(null); // null=checking
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [params, setParams] = useSearchParams();

  // New: create appuntamento
  const [createOpen, setCreateOpen] = useState(false);
  const [appt, setAppt] = useState({
    summary: "",
    date: "",
    startTime: "10:00",
    endTime: "11:00",
    location: "",
    description: "",
  });
  const [creating, setCreating] = useState(false);

  const createAppt = async () => {
    if (!appt.summary.trim()) return toast.error("Titolo richiesto");
    if (!appt.date) return toast.error("Data richiesta");
    setCreating(true);
    try {
      // Build local ISO with timezone offset
      const tzOffset = new Date().getTimezoneOffset();
      const sign = tzOffset <= 0 ? "+" : "-";
      const abs = Math.abs(tzOffset);
      const hh = String(Math.floor(abs / 60)).padStart(2, "0");
      const mm = String(abs % 60).padStart(2, "0");
      const tz = `${sign}${hh}:${mm}`;
      const startISO = `${appt.date}T${appt.startTime}:00${tz}`;
      const endISO = `${appt.date}T${appt.endTime}:00${tz}`;
      await api.post("/calendar/events", {
        summary: appt.summary.trim(),
        start: startISO,
        end: endISO,
        location: appt.location || null,
        description: appt.description || null,
        all_day: false,
      });
      toast.success("Appuntamento creato su Google Calendar");
      setCreateOpen(false);
      setAppt({ summary: "", date: "", startTime: "10:00", endTime: "11:00", location: "", description: "" });
      // Refresh events
      try { await fetchEvents(); } catch {}
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione evento");
    } finally {
      setCreating(false);
    }
  };

  const grid = useMemo(() => buildMonthGrid(cursor.y, cursor.m), [cursor]);
  const today = new Date();
  const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const checkStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/status");
      setConnected(!!data.connected);
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    const flag = params.get("gcal");
    if (flag === "connected") {
      toast.success("Google Calendar collegato");
      checkStatus();
      params.delete("gcal");
      setParams(params, { replace: true });
    } else if (flag === "error") {
      toast.error("Errore collegamento Google Calendar");
      params.delete("gcal");
      params.delete("reason");
      setParams(params, { replace: true });
    }
  }, [params, setParams, checkStatus]);

  const fetchEvents = useCallback(async () => {
    if (!connected) return;
    setLoadingEvents(true);
    try {
      const first = new Date(cursor.y, cursor.m, 1).toISOString();
      const last = new Date(cursor.y, cursor.m + 1, 1).toISOString();
      const { data } = await api.get("/calendar/events", {
        params: { time_min: first, time_max: last },
      });
      setEvents(data.items || []);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Sessione Google scaduta, ricollega");
        setConnected(false);
      } else {
        toast.error("Errore lettura eventi Google");
      }
    } finally {
      setLoadingEvents(false);
    }
  }, [connected, cursor]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const eventsByDay = useMemo(() => {
    const m = new Map();
    for (const ev of events) {
      const k = eventDateKey(ev);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(ev);
    }
    return m;
  }, [events]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/oauth/calendar/login");
      window.location.href = data.authorization_url;
    } catch {
      toast.error("Errore avvio login Google");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Scollegare Google Calendar?")) return;
    try {
      await api.post("/calendar/disconnect");
      setConnected(false);
      setEvents([]);
      toast.success("Google Calendar scollegato");
    } catch {
      toast.error("Errore scollegamento");
    }
  };

  const prevMonth = () => setCursor(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  const nextMonth = () => setCursor(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));

  return (
    <div className="space-y-6 tp-fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Calendario</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-1">
            Shooting
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Pianifica gli shooting e sincronizzali col tuo Google Calendar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {connected && (
            <>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="gcal-create-event-button"
                    className="tp-brand-btn h-10 rounded-full"
                  >
                    <Plus size={14} className="mr-1.5" /> Crea appuntamento
                  </Button>
                </DialogTrigger>
                <DialogContent className="tp-glass-strong border-white/10 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-white">Nuovo appuntamento Google Calendar</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Titolo</Label>
                      <Input
                        data-testid="appt-summary-input"
                        value={appt.summary}
                        onChange={(e) => setAppt({ ...appt, summary: e.target.value })}
                        className="bg-white/5 border-white/10 text-white h-11"
                        placeholder="Es. Shooting cliente X"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Data</Label>
                      <Input
                        data-testid="appt-date-input"
                        type="date"
                        value={appt.date}
                        onChange={(e) => setAppt({ ...appt, date: e.target.value })}
                        className="bg-white/5 border-white/10 text-white h-11"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-zinc-400">Inizio</Label>
                        <Input
                          data-testid="appt-start-input"
                          type="time"
                          value={appt.startTime}
                          onChange={(e) => setAppt({ ...appt, startTime: e.target.value })}
                          className="bg-white/5 border-white/10 text-white h-11"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-zinc-400">Fine</Label>
                        <Input
                          data-testid="appt-end-input"
                          type="time"
                          value={appt.endTime}
                          onChange={(e) => setAppt({ ...appt, endTime: e.target.value })}
                          className="bg-white/5 border-white/10 text-white h-11"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">
                        Luogo (opzionale)
                      </Label>
                      <Input
                        data-testid="appt-location-input"
                        value={appt.location}
                        onChange={(e) => setAppt({ ...appt, location: e.target.value })}
                        className="bg-white/5 border-white/10 text-white h-11"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">
                        Note (opzionale)
                      </Label>
                      <Input
                        data-testid="appt-desc-input"
                        value={appt.description}
                        onChange={(e) => setAppt({ ...appt, description: e.target.value })}
                        className="bg-white/5 border-white/10 text-white h-11"
                      />
                    </div>
                    <Button
                      data-testid="appt-save-button"
                      onClick={createAppt}
                      disabled={creating}
                      className="tp-brand-btn w-full rounded-xl"
                    >
                      {creating ? "Creo..." : "Crea appuntamento"}
                    </Button>
                    <div className="text-[11px] text-zinc-500 text-center">
                      Se ricevi errore permessi, ricollega Google Calendar per abilitare la scrittura.
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Button
                onClick={fetchEvents}
                data-testid="gcal-refresh-button"
                disabled={loadingEvents}
                className="h-10 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/10"
              >
                <RefreshCw size={14} className={`mr-1.5 ${loadingEvents ? "animate-spin" : ""}`} />
                Aggiorna
              </Button>
              <Button
                onClick={disconnect}
                data-testid="gcal-disconnect-button"
                className="h-10 rounded-full bg-white/5 hover:bg-red-500/15 text-white border border-white/10 hover:text-red-300"
              >
                <Unlink size={14} className="mr-1.5" /> Scollega
              </Button>
            </>
          )}
          {connected === false && (
            <Button
              data-testid="google-connect-button"
              onClick={connect}
              disabled={connecting}
              className="tp-brand-btn h-10 rounded-full"
            >
              <LinkIcon size={14} className="mr-1.5" />
              {connecting ? "Reindirizzo…" : "Collega Google Calendar"}
            </Button>
          )}
        </div>
      </div>

      <div className="tp-glass rounded-3xl overflow-hidden">
        <div className="px-5 sm:px-6 py-4 flex items-center gap-3 border-b border-white/5">
          <CalendarDays size={16} className="text-fuchsia-300" />
          <h2 className="text-lg font-semibold text-white">
            {MONTHS[cursor.m]} <span className="text-zinc-500 font-normal">{cursor.y}</span>
          </h2>
          {connected && (
            <span className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-md px-2 py-0.5" data-testid="gcal-status">
              Sincronizzato
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <button onClick={prevMonth} data-testid="cal-prev" className="h-8 w-8 rounded-lg text-zinc-300 hover:text-white hover:bg-white/10">‹</button>
            <button
              onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })}
              data-testid="cal-today"
              className="h-8 rounded-lg text-xs text-zinc-300 hover:text-white hover:bg-white/10 px-3"
            >
              Oggi
            </button>
            <button onClick={nextMonth} data-testid="cal-next" className="h-8 w-8 rounded-lg text-zinc-300 hover:text-white hover:bg-white/10">›</button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-white/5">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 text-center">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {grid.map((cell, i) => {
            const isToday = cell.date === todayISO;
            const dayEvents = cell.date ? eventsByDay.get(cell.date) || [] : [];
            return (
              <div
                key={i}
                data-testid={cell.date ? `cal-day-${cell.date}` : undefined}
                className={`min-h-[100px] border-r border-b border-white/5 last:border-r-0 p-2 text-sm relative ${
                  cell.outside ? "text-zinc-700 bg-white/[0.01]" : "text-zinc-200 hover:bg-white/[0.03] transition-colors"
                }`}
              >
                <div
                  className={`inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-md tabular-nums text-xs ${
                    isToday ? "tp-brand-gradient text-white font-semibold shadow-md shadow-fuchsia-600/40" : ""
                  }`}
                >
                  {cell.day}
                </div>
                {dayEvents.length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <a
                        key={ev.id}
                        href={ev.html_link}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`gcal-event-${ev.id}`}
                        title={`${eventTimeLabel(ev)} · ${ev.summary}`}
                        className="block truncate rounded-md px-1.5 py-0.5 text-[10px] tp-brand-gradient text-white shadow-sm"
                      >
                        {!ev.all_day && (
                          <span className="opacity-80 mr-1">{eventTimeLabel(ev)}</span>
                        )}
                        {ev.summary}
                      </a>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-zinc-500">+{dayEvents.length - 3} altri</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {connected === false && (
        <div className="tp-glass rounded-3xl p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl tp-brand-gradient shadow-lg shadow-fuchsia-600/30 flex items-center justify-center mb-4">
            <Camera className="text-white" />
          </div>
          <h3 className="text-xl font-semibold text-white">Google Calendar non collegato</h3>
          <p className="text-sm text-zinc-400 mt-1 max-w-md mx-auto">
            Collega il tuo account per vedere gli eventi del tuo calendario direttamente qui.
          </p>
          <Button
            onClick={connect}
            disabled={connecting}
            data-testid="google-connect-cta"
            className="tp-brand-btn mt-5 rounded-full"
          >
            <LinkIcon size={14} className="mr-1.5" /> Collega Google Calendar
          </Button>
        </div>
      )}
    </div>
  );
}
